// @flow

import * as React from 'react';
import classNames from 'classnames';
import Icons from '../../../../styles/Icons';

type Props = {
  /**
   * The content that will appear when box is open.
   */
  children: React.Element<any> | Array<React.Element<any>>,
  /**
   * Callback from parent that is called whenever box is clicked.
   */
  onClick?: () => {},
  /**
   * Determines if box is open or not (passed in by parent, box itself is stateless).
   */
  opened: boolean,
  /**
   * For QA testing purposes, actual data-testid will be suffixed with "-box"
   */
  testid: string,
  /**
   * For overriding/adding styles, applied to outer-most div
   */
  className?: string,
  /**
   * For overriding/adding styles, applied to div around children content
   */
  contentClassName?: string,
  /**
   * If true, clicking ANYWHERE on the box will NOT trigger this.props.onClick
   * The entirety of the box will be greyed out.
   */
  disabled?: boolean,
  /**
   * If true, clicking ANYWHERE on the box will NOT trigger this.props.onClick
   * Only the arrow on the left side will appear greyed out.
   *
   * This would be used instead of 'disabled' prop if you have
   * a button that needs to remain clickable but you don't want the box
   * to open or close.
   */
  disableArrowOnly?: boolean,
  /**
   * If true, no arrow will appear on the left side of the box.
   */
  hideArrow?: boolean,
  /**
   * For overriding/adding styles, applied to div around arrow
   */
  arrowClassName?: string,
  /**
   * For overriding displayed arrow when the box is open
   */
  openArrow?:
    | 'keyboard_arrow_down'
    | 'keyboard_arrow_left'
    | 'keyboard_arrow_right'
    | 'keyboard_arrow_up',
  /**
   * For overriding displayed arrow when the box is closed
   */
  closedArrow?:
    | 'keyboard_arrow_down'
    | 'keyboard_arrow_left'
    | 'keyboard_arrow_right'
    | 'keyboard_arrow_up',
  /**
   * Optional element to be displayed on the right end when box is opened
   * OR closed & success is false (example is BinaryToggle buttons).
   */
  rightSideElement?: React.Element<any>,
  /**
   * Appears to left side of box.
   */
  title?: string | React.Element<any>,
  /**
   * If true, green check mark will show on right end in the following scenarios:
   * 1. Always shown if there is no rightSideElement, or
   * 2. If there is a rightSideElement, green checkmark will only show when
   * the box is closed AND this 'success' prop is true
   * (otherwise rightSideElement shows)
   */
  success?: boolean
};
class CollapsibleBox extends React.Component<Props> {
  static defaultProps = {
    opened: false,
    title: null,
    success: false,
    disabled: false,
    disableArrowOnly: false,
    hideArrow: false,
    testid: 'collapsible'
  };

  render() {
    const {
      className,
      contentClassName,
      onClick,
      disableArrowOnly,
      hideArrow,
      opened,
      title,
      success,
      disabled,
      children,
      testid,
      arrowClassName,
      openArrow,
      closedArrow
    } = this.props;

    let rightSideElement = this.props.rightSideElement;
    if (!rightSideElement || (!opened && success)) {
      rightSideElement = (
        <i
          className={classNames('material-icons collapsible-box__green-check', {
            'h_d-n': !success
          })}
        >
          done
        </i>
      );
    }

    return (
      <div
        className={classNames('collapsible-box', className, {
          'collapsible-box__disabled': disabled
        })}
        data-testid={`${testid}-box`}
      >
        <div
          className={classNames('collapsible-box__header', {
            'h_cur-p': !!onClick && !disabled && !disableArrowOnly
          })}
          onClick={disabled || disableArrowOnly ? null : onClick}
        >
          <div
            className={classNames(
              'collapsible-box__box-arrow',
              arrowClassName,
              {
                noBackground: hideArrow,
                'collapsible-box__disabled': disableArrowOnly
              }
            )}
            data-testid={`${testid}-arrow`}
          >
            <i className={classNames('material-icons', { 'h_d-n': hideArrow })}>
              {opened
                ? openArrow || Icons.KEYBOARD_ARROW_DOWN
                : closedArrow || Icons.KEYBOARD_ARROW_RIGHT}
            </i>
          </div>
          <div className="collapsible-box__title">
            <span className="collapsible-box__title-text">{title}</span>
            {rightSideElement}
          </div>
        </div>
        <div
          className={classNames('collapsible-box__content', contentClassName, {
            active: opened
          })}
        >
          {children}
        </div>
      </div>
    );
  }
}

export default CollapsibleBox;
